import { useState } from 'react'
import { useParams } from 'react-router'
import { AlertTriangle, Calculator, Search } from 'lucide-react'
import { ErrorState, FormDialog, PageHeader, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import {
  useJournals, usePayrollLines, usePayrollRun, usePayrollRunEvents, usePayrollRunMutations,
} from '../api'
import { ExplainDialog } from '../components/ExplainDialog'
import { PayslipPanel } from '../components/PayslipPanel'
import { monthName, PAYROLL_STATUS_LABELS, PAYROLL_STATUS_NOTES } from '../types'
import type { PayrollStatus } from '../types'

type Tab = 'register' | 'journals' | 'history'

/** Which endpoint each onward move calls, and what the button should say. */
const MOVES: Partial<Record<PayrollStatus, { label: string; action: string }>> = {
  CALCULATED: { label: 'Recalculate', action: 'calculate' },
  LOCKED: { label: 'Lock for review', action: 'lock' },
  APPROVED: { label: 'Approve', action: 'approve' },
  PAID: { label: 'Mark as paid', action: 'mark-paid' },
  CLOSED: { label: 'Close the period', action: 'close' },
  OPEN: { label: 'Reopen', action: 'reopen' },
}

export function PayrollRunPage() {
  useBreadcrumb()
  const { id = '' } = useParams()
  const run = usePayrollRun(id)
  const [tab, setTab] = useState<Tab>('register')
  const lines = usePayrollLines(id, tab === 'register')
  const journals = useJournals(id, tab === 'journals')
  const events = usePayrollRunEvents(id)
  const mutations = usePayrollRunMutations()

  const [explaining, setExplaining] = useState<string | null>(null)
  const [payslipFor, setPayslipFor] = useState<string | null>(null)
  const [reopenReason, setReopenReason] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (run.isError) return <ErrorState error={run.error} onRetry={() => void run.refetch()} />
  if (run.isPending || !run.data) return <Skeleton className="h-96 w-full" />

  const current = run.data

  async function move(target: PayrollStatus) {
    setError(null)
    try {
      if (target === 'OPEN') { setReopenReason(''); return }
      if (target === 'CALCULATED') { await mutations.calculate.mutateAsync(id); return }
      if (target === 'LOCKED') { await mutations.lock.mutateAsync({ id }); return }
      if (target === 'APPROVED') { await mutations.approve.mutateAsync({ id }); return }
      if (target === 'PAID') { await mutations.markPaid.mutateAsync({ id }); return }
      if (target === 'CLOSED') { await mutations.close.mutateAsync({ id }); return }
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That could not be done')
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={monthName(current.periodYear, current.periodMonth)}
        subtitle={PAYROLL_STATUS_NOTES[current.status]}
        actions={
          <span className="flex flex-wrap gap-2">
            {current.status === 'OPEN' && (
              <Button type="button" onClick={() => void move('CALCULATED')}>
                <Calculator className="size-4" aria-hidden="true" />
                Calculate
              </Button>
            )}
            {current.allowedNext.map((target) => (
              <Button
                key={target}
                type="button"
                variant={target === 'OPEN' ? 'outline' : 'primary'}
                onClick={() => void move(target)}
              >
                {MOVES[target]?.label ?? PAYROLL_STATUS_LABELS[target]}
              </Button>
            ))}
          </span>
        }
      />

      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Card className="p-4">
          <p className="text-sm text-slate-600">Status</p>
          <StatusBadge status={current.status} label={PAYROLL_STATUS_LABELS[current.status]} />
        </Card>
        <Card className="p-4">
          <p className="text-sm text-slate-600">People</p>
          <p className="text-lg font-semibold">{current.employeeCount}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-slate-600">Gross</p>
          <p className="text-lg font-semibold">{current.totalGross}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-slate-600">Net</p>
          <p className="text-lg font-semibold">{current.totalNet}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-slate-600">Employer&rsquo;s cost</p>
          <p className="text-lg font-semibold">{current.totalEmployerCost}</p>
        </Card>
      </div>

      {current.reopenCount > 0 && (
        <p role="note" className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          This run has been reopened {current.reopenCount} time
          {current.reopenCount === 1 ? '' : 's'}. If it had been paid, the original journal was
          reversed rather than edited — both entries are on the journals tab.
        </p>
      )}

      <div className="flex gap-2 border-b border-slate-200">
        {(['register', 'journals', 'history'] as Tab[]).map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={tab === name}
            className={`px-3 py-2 text-sm capitalize ${tab === name
              ? 'border-b-2 border-[var(--color-primary)] font-medium text-[var(--color-primary)]'
              : 'text-slate-600'}`}
            onClick={() => setTab(name)}
          >
            {name}
          </button>
        ))}
      </div>

      {tab === 'register' && (
        <Card>
          <p className="border-b border-slate-200 p-3 text-sm text-slate-600">
            Opening the register is recorded in the sensitive access log — it discloses what
            everybody is paid.
          </p>
          {lines.isPending && <Skeleton className="m-4 h-24" />}
          {lines.isSuccess && lines.data.length === 0 && (
            <p className="p-4 text-sm text-slate-600">
              Nothing has been calculated yet.
            </p>
          )}
          {lines.isSuccess && lines.data.length > 0 && (
            <div className="max-h-[32rem] overflow-auto overscroll-contain">
              <table className="w-full text-sm">
                <thead className="sticky top-0 border-b border-slate-200 bg-white text-slate-600">
                  <tr>
                    <th className="p-2 text-start font-medium">Person</th>
                    <th className="p-2 text-start font-medium">Element</th>
                    <th className="p-2 text-end font-medium">Amount</th>
                    <th className="p-2" />
                  </tr>
                </thead>
                <tbody>
                  {lines.data.map((line) => (
                    <tr key={line.id} className="border-b border-slate-100">
                      <td className="p-2">
                        <button
                          type="button"
                          className="underline underline-offset-2"
                          onClick={() => setPayslipFor(line.employeeId)}
                        >
                          {line.employeeName ?? line.employeeCode}
                        </button>
                      </td>
                      <td className="p-2 text-slate-600">{line.elementName ?? line.elementCode}</td>
                      <td className="p-2 text-end">{line.amount}</td>
                      <td className="p-2 text-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label="Why is this figure what it is"
                          onClick={() => setExplaining(line.id)}
                        >
                          <Search className="size-3.5" aria-hidden="true" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === 'journals' && (
        <div className="space-y-3">
          {journals.isPending && <Skeleton className="h-32 w-full" />}
          {journals.isSuccess && journals.data.length === 0 && (
            <Card className="p-4 text-sm text-slate-600">
              Nothing has been posted. The journal is written when the run is approved.
            </Card>
          )}
          {journals.data?.map((batch) => (
            <Card key={batch.id} className="p-4">
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="font-medium">
                  {batch.batchType.replace('_', ' ').toLowerCase()}
                  {batch.reversesBatchId && ' — reverses an earlier batch'}
                </h3>
                <StatusBadge status={batch.status} />
              </div>
              <p className="mb-2 text-sm text-slate-600">{batch.notes}</p>
              {batch.unmappedLineCount > 0 && (
                <p role="note" className="mb-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900">
                  {batch.unmappedLineCount} line
                  {batch.unmappedLineCount === 1 ? ' has' : 's have'} no GL account. Elements
                  carry their own account code; a chart-of-accounts mapping arrives in part 2.
                  Nothing is posted to a made-up account in the meantime.
                </p>
              )}
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 text-slate-600">
                  <tr>
                    <th className="p-2 text-start font-medium">Account</th>
                    <th className="p-2 text-end font-medium">Debit</th>
                    <th className="p-2 text-end font-medium">Credit</th>
                    <th className="p-2 text-start font-medium">Memo</th>
                  </tr>
                </thead>
                <tbody>
                  {batch.lines.map((line) => (
                    <tr key={line.id} className="border-b border-slate-100">
                      <td className="p-2 font-mono text-xs">
                        {line.accountCode}
                        {line.unmapped && <span className="ms-2 text-amber-700">unmapped</span>}
                      </td>
                      <td className="p-2 text-end">{line.debit || ''}</td>
                      <td className="p-2 text-end">{line.credit || ''}</td>
                      <td className="p-2 text-slate-600">{line.memo}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-medium">
                    <td className="p-2">Total</td>
                    <td className="p-2 text-end">{batch.totalDebit}</td>
                    <td className="p-2 text-end">{batch.totalCredit}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </Card>
          ))}
        </div>
      )}

      {tab === 'history' && (
        <Card>
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-slate-600">
              <tr>
                <th className="p-3 text-start font-medium">When</th>
                <th className="p-3 text-start font-medium">Move</th>
                <th className="p-3 text-start font-medium">Who</th>
                <th className="p-3 text-start font-medium">Reason</th>
              </tr>
            </thead>
            <tbody>
              {events.data?.map((event) => (
                <tr key={event.id} className="border-b border-slate-100">
                  <td className="p-3">{event.occurredAt.replace('T', ' ').slice(0, 19)}</td>
                  <td className="p-3">{event.fromStatus ?? '—'} → {event.toStatus}</td>
                  <td className="p-3 text-slate-600">{event.actorEmail ?? '—'}</td>
                  <td className="p-3 text-slate-600">{event.reason ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {payslipFor && (
        <PayslipPanel runId={id} employeeId={payslipFor} onExplain={setExplaining} />
      )}

      <ExplainDialog lineId={explaining} onClose={() => setExplaining(null)} />

      <FormDialog
        open={reopenReason != null}
        onOpenChange={(open) => { if (!open) setReopenReason(null) }}
        title="Reopen this run"
        description="A paid run's journal is reversed rather than edited, and the correction is posted when it is approved again."
        submitLabel="Reopen it"
        loading={mutations.reopen.isPending}
        error={error}
        onSubmit={() => {
          void (async () => {
            try {
              await mutations.reopen.mutateAsync({ id, reason: reopenReason ?? '' })
              setReopenReason(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That run could not be reopened')
            }
          })()
        }}
      >
        <div>
          <Label htmlFor="reopen-reason">Why</Label>
          <Input
            id="reopen-reason"
            value={reopenReason ?? ''}
            placeholder="A late timesheet correction for two people"
            onChange={(event) => setReopenReason(event.target.value)}
          />
          <p className="mt-1 text-sm text-slate-600">
            Recorded against the run. Reopening is counted and shows on the history tab.
          </p>
        </div>
      </FormDialog>
    </div>
  )
}
